<template>
    <v-card>
        <v-alert
            v-show="error != ''"
            color="#4c9aff"
            border="left"
            colored-border
            type="error"
            class="mb-0"
        >
            {{ error }}
        </v-alert>

        <v-card-text class="grey lighten-5">
            <div class="text-center mb-4" v-if="pulledBy">
                <h3>Pulled By: {{ pulledBy }}</h3>
            </div>
            <v-row>
                <v-col v-for="item in items" :key="item.cartId" cols="12">
                    <v-card>
                        <v-card-title style="justify-content: center;"
                            ><barcode
                                v-bind:value="item.barcode"
                                format="CODE128"
                                display-value="false"
                                height="50"
                            >
                            </barcode
                        ></v-card-title>
                        <v-card-text>
                            <p>Order {{ item.cartId }}</p>
                            <p>Total Units {{ item.units }}</p>
                            <p>
                                Gift message -
                                {{ item.giftMessage !== '' ? 'Yes' : 'No' }}
                            </p>
                            <div
                                v-if="
                                    item.verificationItems &&
                                        item.verificationItems.length
                                "
                            >
                                <div
                                    v-for="vi in getFilteredVerificationItems(
                                        item.verificationItems
                                    )"
                                    :key="vi.orderItem.id"
                                >
                                    {{ vi.orderItem.sku }} - ${{
                                        vi.orderItem.price
                                    }}
                                    - QTY - {{ vi.orderItem.quantity }}
                                </div>
                            </div>
                            <p v-show="item.shipLaterDate != ''">
                                Ship Later Date - {{ item.shipLaterDate }}
                            </p>
                        </v-card-text>
                    </v-card></v-col
                ></v-row
            >
        </v-card-text>
        <v-card-actions class="d-print-none">
            <v-btn color="primary" type="button" @click.prevent="printCartIds"
                >Print Cart ID's</v-btn
            >
            <v-btn
                color="primary"
                type="button"
                @click.prevent="printGiftMessages"
                >Print Gift Messages</v-btn
            >
        </v-card-actions>
    </v-card>
</template>

<script>
import VueBarcode from 'vue-barcode';
import * as data from '@/data/index';
import { jsPDF } from 'jspdf';

export default {
    props: {
        orders: {
            type: Array,
            default() {
                return [];
            }
        }
    },
    data() {
        return {
            error: '',
            items: [],
            shipStationPrefix: '',
            batchOrderGiftMessages: [],
            pulledBy: '',
            page: {
                size: {
                    width: 11,
                    height: 8.5
                },
                margins: {
                    top: 2.16,
                    right: 2.75,
                    bottom: 1,
                    left: 0.81
                }
            }
        };
    },
    components: { barcode: VueBarcode },
    async mounted() {
        const self = this;
        if (self.orders.length === 0) {
            this.$router.push({
                name: 'Home',
                params: {
                    alert_message: 'No orders found in the batch!',
                    show_alert: true
                }
            });
        } else {
            // Check and update prices for orders missing price data
            await this.ensureOrderItemsHavePrices();
            
            // Get the picker's name from the first order's history
            await this.getUserWhoStartedPulling();

            this.items = this.orders.map(order => {
                return {
                    barcode: order.order.id,
                    cartId: order.order.id,
                    units: order.itemCount,
                    giftMessage: self.getOrderGiftMessages(order),
                    shipLaterDate: self.getShipLaterDate(order),
                    verificationItems: order.verification.items
                };
            });
        }
    },
    methods: {
        getFilteredVerificationItems(verificationItems) {
            if (!verificationItems) return [];
            return verificationItems.filter(
                vi => vi.orderItem.price && vi.orderItem.price > 0
            );
        },

        async ensureOrderItemsHavePrices() {
            const ordersNeedingPrices = [];

            // Check which orders are missing price data
            for (const order of this.orders) {
                if (order.verification && order.verification.items) {
                    const hasMissingPrice = order.verification.items.some(
                        item =>
                            !item.orderItem ||
                            item.orderItem.price === undefined
                    );
                    if (hasMissingPrice) {
                        ordersNeedingPrices.push(order.order.id);
                    }
                }
            }

                // Update prices for orders that need them
                if (ordersNeedingPrices.length > 0) {
                    console.log(
                        `Updating prices for ${ordersNeedingPrices.length} orders`
                    );
                    
                    // Get order products from BigCommerce for each order
                    const getOrderProductsUrl = `${process.env.VUE_APP_FIREBASE_FUNCTION_ROOT}/getOrderProducts`;
                    const priceUpdatePromises = ordersNeedingPrices.map(async orderId => {
                        const response = await fetch(getOrderProductsUrl + '?id=' + orderId);
                        const orderItems = await response.json();
                        
                        // Update each item's price through store
                        const itemUpdatePromises = orderItems.map(item => 
                            this.$store.dispatch('updateVerificationItem', {
                                orderId: orderId,
                                itemId: item.id,
                                data: {
                                    'orderItem.price': item.total_inc_tax || 0
                                }
                            })
                        );
                        
                        await Promise.all(itemUpdatePromises);
                    });
                    
                    await Promise.all(priceUpdatePromises);

                // Reload verification data for updated orders
                const updatePromises = ordersNeedingPrices.map(async orderId => {
                    const updatedVerification = await data.orders.getVerificationDataById(
                        orderId,
                        this.$store.state.currentUser
                    );
                    
                    // Find and update the order in this.orders
                    const order = this.orders.find(o => o.order.id === orderId);
                    if (order) {
                        order.verification = updatedVerification;
                    }
                });
                
                await Promise.all(updatePromises);
            }
        },

        getShipLaterDate(order) {
            return data.orders.getOrderShipLaterDate(order);
        },

        printCartIds() {
            window.print();
        },

        getOrderGiftMessages(order) {
            const giftMessage = data.orderGiftMessage.checkAndGetGiftMessage(
                order
            );
            if (giftMessage !== '') {
                this.batchOrderGiftMessages.push({
                    orderId: order.order.id,
                    giftMessage: giftMessage
                });
            }
            return giftMessage;
        },

        async getUserWhoStartedPulling() {
            try {
                // Get the first order from the batch
                if (!this.orders || this.orders.length === 0) return;
                
                const firstOrder = this.orders[0];
                const historySnapshot = await data.orderHistory.get(
                    firstOrder.order.id
                );

                // Sort by created ascending to get the first "pulling started" entry
                const docs = historySnapshot.docs.sort((a, b) => {
                    return a.data().created - b.data().created;
                });

                for (const doc of docs) {
                    const docData = doc.data();
                    if (
                        docData.description &&
                        docData.description.startsWith(
                            'Order pulling is started'
                        )
                    ) {
                        this.pulledBy = docData.user || '';
                        break;
                    }
                }
            } catch (error) {
                console.log('Error getting user who started pulling:', error);
            }
        },

        printGiftMessages() {
            const self = this;
            const doc = new jsPDF({
                orientation: 'landscape',
                unit: 'in',
                format: [self.page.size.width, self.page.size.height]
            });
            doc.setFont('Times', 'bolditalic');
            doc.setFontSize(16);
            let i = 1;

            self.batchOrderGiftMessages.map(item => {
                let orderId = item.orderId;
                let giftMessage = item.giftMessage;
                if (giftMessage !== '') {
                    if (i > 1) {
                        // Each gift message will be printed on a new page.
                        doc.addPage({
                            orientation: 'landscape',
                            unit: 'in',
                            format: [
                                self.page.size.width,
                                self.page.size.height
                            ]
                        });
                    }

                    doc.text(
                        orderId.toString(),
                        doc.internal.pageSize.getWidth() -
                            this.page.margins.right,
                        this.page.margins.top + 0.55,
                        { align: 'right' }
                    );

                    doc.text(
                        giftMessage,
                        (doc.internal.pageSize.getWidth() -
                            this.page.margins.right) /
                            2,
                        this.page.margins.top + 1.38,
                        { align: 'center' }
                    );
                    i++;
                }
            });
            doc.save('batch_orders_gift_message.pdf');
        }
    }
};
</script>
